import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Plus, Minus, BarChart3, Calendar, Shield, Layers, Globe,
  AlertTriangle, FileText, Cpu, Zap, TrendingUp, Users, Eye, Target,
  Activity, ClipboardCheck, Monitor, ArrowRight, Package, Wrench, Database,
  Truck, Radar, Box, PieChart
} from "lucide-react";
import { CtaForm } from "../components/CtaForm";
import PanorixPerceptionField from "../components/PanorixPerceptionField";
import DrishtiLogo, { DrishtiIcon } from "../components/DrishtiLogo";

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          setVal(end * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ═══════════════════════════════════════════════════════════════
   MODULE & DATA CONSTANTS
   ═══════════════════════════════════════════════════════════════ */
const TIERS = [
  { n: 1, name: "Portfolio & Program", tagline: "The CxO lens. Every project. Every rupee." },
  { n: 2, name: "Schedule & Progress", tagline: "From Gantt to drone. Progress measured six ways." },
  { n: 3, name: "Construction Execution", tagline: "The worksite. Piping. Structure. Weld by weld." },
  { n: 4, name: "Cost & Commercial", tagline: "Every rupee, tracked. Every claim, defensible." },
  { n: 5, name: "Material & Delivery", tagline: "Material on site at the right moment." },
  { n: 6, name: "Command & Cognition", tagline: "The integrated gaze. Reasoning built in." },
];

const MODULES = [
  { tier: 1, slug: "portfolio-dashboard", name: "Portfolio Dashboard", Icon: Monitor, body: "Every project, one CxO view. Cross-project KPIs, stage-gate status, financial roll-ups, portfolio risk heatmap, board-ready exports." },
  { tier: 1, slug: "program-management", name: "Program Management", Icon: Layers, body: "Master schedule across multi-project programs. Inter-project dependencies, shared-resource optimization, program-level EVM, critical-chain across projects." },
  { tier: 1, slug: "raid-management", name: "RAID Management", Icon: AlertTriangle, body: "Risks · Assumptions · Issues · Dependencies — governed. Severity matrices, owner assignment, SLA-driven resolution, cross-project dependency visualization, Monte Carlo risk quantification." },
  { tier: 2, slug: "scheduling", name: "Scheduling Engine (CPM/Gantt)", Icon: Calendar, body: "Full CPM forward/backward pass, float calculation, resource-loaded schedule, legacy-scheduler XER import/export, legacy schedulers round-trip via MPP, baseline management." },
  { tier: 2, slug: "vpr", name: "VPR — Visual Progress Referential", Icon: Eye, body: "Autodesk-class CAD/BIM BIM stack 3D viewer integration, color-coded progress by unit/system/PBS node, click-through from 3D to schedule activity, progress heatmaps by discipline." },
  { tier: 2, slug: "drone-lidar-progress", name: "Drone & LiDAR Progress", Icon: Radar, body: "Drone-photo and LiDAR point-cloud ingestion, auto-alignment to design model, as-built vs as-planned deviation heatmaps, volumetric progress (earthworks, concrete, stockpiles), AI object detection." },
  { tier: 3, slug: "workfront-forecasting", name: "Workfront Forecasting (Piping & Structural)", Icon: Target, body: "AWP-based IWP readiness scoring against 8 constraints (engineering, materials, tools, equipment, labor, permits, access, predecessor). Rolling 3-week lookahead. Crew-level workability prediction." },
  { tier: 3, slug: "fabrication-erection", name: "Fabrication & Erection Progress", Icon: Wrench, body: "Spool-level fabrication tracking with QR/RFID. Weld-map progress per ISO/per weld. NDT integration. Erection by PBS node. Piping isometric completion. Steel tonnage erected vs planned." },
  { tier: 3, slug: "5d-monitoring", name: "5D Monitoring", Icon: Box, body: "Cost-loaded 4D simulation — every 3D element carries schedule and cost. Visual cost-loaded progress. Identify cost-to-progress imbalances. Animate capex burn-rate against physical progress." },
  { tier: 4, slug: "cost-control", name: "Cost Control (EVM)", Icon: BarChart3, body: "Full EVM with CPI, SPI, CV, SV, EAC, ETC, TCPI. Period-over-period trending, baseline vs actual, cost-breakdown structure (CBS) drill-down." },
  { tier: 4, slug: "invoice-management", name: "Invoice & Working Capital", Icon: Package, body: "Vendor invoice capture, PO-GR-IR three-way match, retention management, LD/bonus calculations, cash-flow forecasting, working capital dashboards." },
  { tier: 4, slug: "contracts-claims", name: "Contracts & Claims (CLM)", Icon: FileText, body: "Contract authoring with clause libraries, variation/change-order management, claims drafting with GenAI assistance, counter-claim analysis, delay analysis linked to schedule." },
  { tier: 5, slug: "material-management", name: "Material Management", Icon: Truck, body: "MR workflow integrated with engineering BOM/QTO. MRR with QR/RFID scanning. Warehouse slot management. Laydown yard optimization. JIT delivery aligned with Workfront. Surplus/shortage analytics." },
  { tier: 5, slug: "hse", name: "HSE Management", Icon: Shield, body: "Incident reporting, near-miss tracking, PTW, JSA/HIRA workflows, HSE observation cards, leading-indicator dashboards, toolbox talk attendance, contractor safety performance, BOCW/OSHA/PESO compliance." },
  { tier: 5, slug: "quality", name: "Quality & Handover", Icon: ClipboardCheck, body: "ITP management, NCR, punch-list, system turnover packages, mechanical-completion dashboards, pre-commissioning checklists, vendor document final acceptance." },
  { tier: 6, slug: "command-centre", name: "Command Centre", Icon: Cpu, body: "Executive dashboard integrating all 17 other modules. Custom widgets, role-based views (Owner, EPC, PMC, Site), alert engine, drill-down to any module in one click." },
  { tier: 6, slug: "cognitive-layer", name: "Cognitive Layer (Cognara)", Icon: Zap, body: "Natural-language queries across all 18 modules in English or Hindi. Cognitive reasoning across RAID, cost, schedule, HSE, materials, welds. Integrated with Cognara GenAI Agent." },
  { tier: 6, slug: "reports-analytics", name: "Reports & Analytics Studio", Icon: PieChart, body: "Configurable reporting: daily site report, weekly progress, monthly board pack, quarterly steering. PDF/PPT/XLSX export. Scheduled auto-generation. AI-drafted executive summaries." },
];

const FAQS = [
  { q: "How is Panorix different from enterprise ERP Legacy scheduler or plant-engineering platforms SYNCHRO?", a: "They are scheduling-and-4D tools, not control towers. You'd still need enterprise ERP for cost, a separate CLM for contracts, Documentum for documents. Panorix is the integrated command centre; these remain upstream data sources that Panorix orchestrates." },
  { q: "Can Panorix replace our existing legacy schedulers?", a: "Panorix's Scheduling Engine has full CPM parity with legacy schedulers. Most clients keep legacy schedulers for planners and use Panorix for project controls and executive views. XER and MPP round-trip supported." },
  { q: "What's the typical deployment timeline?", a: "8-16 weeks for standard deployment. Start with Portfolio Dashboard + 3-4 core modules in weeks 1-6; layer Schedule & Progress modules (VPR, Drone, 4D) in weeks 6-10; add Workfront Forecasting, Material Management, and 5D in weeks 10-14. Enterprise-wide rollouts: 6-9 months." },
  { q: "How does the AI handle sensitive project data?", a: "Three modes: shared Azure cloud with tenant isolation, dedicated Azure with India data residency, or on-premise for Defense/Nuclear/PSU. AI inference can route to self-hosted LLMs." },
  { q: "Can Panorix integrate with our enterprise ERP installation?", a: "Yes. Pre-built enterprise ERP connectors for PS, PM, MM, and FI/CO. Typical integration: 4-6 weeks." },
  { q: "What about legacy legacy schedulers data?", a: "Full bidirectional sync via XER files. Historical projects imported preserving calendars, resources, baselines, and relationships." },
  { q: "How does Cognara handle contracts and claims?", a: "RAG over your contract corpus via MongoDB Atlas Vector Search. Drafts claim letters with clause citations, flags risky language, compares clauses across your portfolio." },
  { q: "What's the pricing model?", a: "Three models: (1) Per user per module (₹24-60 Lakh/yr for 50 users, 6 modules), (2) Project-based (₹1-3 Cr for ₹5,000 Cr project), (3) Enterprise agreement (₹5-25 Cr/yr)." },
  { q: "Is Panorix only for large projects?", a: "No. Scales from ₹100 Cr brownfield revamps (3-4 modules, 12 weeks) to ₹50,000 Cr metro programs (18 modules, portfolio-wide)." },
  { q: "What support and training is included?", a: "24×7 support, named CSM, 4-week onboarding, 5 training seats on Capexara Academy, 2 QBRs/year. Premium tier adds dedicated TAM and on-site support." },
  { q: "How does Drone & LiDAR Progress integrate with our existing drone vendors?", a: "Panorix is vendor-agnostic. We integrate with DJI Enterprise, Skydio, Trimble, Leica BLK, and FARO. For LiDAR we support LAS, LAZ, E57, and PLY formats. Captures auto-align to your design model (IFC, RVT, DWG, NWD) with full version history." },
  { q: "What does Workfront Forecasting actually do?", a: "It applies AWP principles to predict which IWPs will be buildable in the next 3 weeks. Scores each IWP against 8 constraints — engineering, materials, tools, equipment, labor, permits, access, predecessor work. Typical outcome: 15-25% crew productivity improvement and 30-40% reduction in standby time." },
];

/* ═══════ PAGE EXPORT ═══════ */
export default function PlatformDrishti() {
  return (
    <>
      <Section1Hero />
      <Section2Problem />
      <Section3Modules />
      <Section4Dashboard />
      <Section5Cognitive />
      <Section6Integrations />
      <Section7Industries />
      <Section8Outcomes />
      <Section9Faq />
      <Section10Cta />
    </>
  );
}

/* ═══════ S1 HERO — matches grantha-hero-grid layout ═══════ */
function Section1Hero() {
  return (
    <section className="grantha-hero" data-testid="dr-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="dr-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="grantha-crumb mono">Platforms / Enterprise Digital / PANORIX</div>
          <div className="eyebrow" style={{ marginTop: 10 }}>Capital Projects Control Tower &amp; Command Centre</div>
          <h1 className="grantha-h1" data-testid="dr-h1">
            <span className="grantha-indo dr-logo-lockup" data-testid="dr-logo">
              <span className="rc-mark-wrap"><DrishtiIcon size={88} /></span>
              <span className="rc-wordmark">
                Pano<span className="rc-c">r</span>ix
              </span>
            </span>
            <span className="grantha-h1-en">The single pane of glass for <em>every rupee, every hour, every risk, every ton, every weld.</em></span>
          </h1>
          <p className="grantha-sub" data-testid="dr-hero-sub">
            Eighteen modules. One command canvas. From ₹100 Cr refinery revamps to ₹50,000 Cr metro corridors — Panorix gives owners, EPCs, and PMCs the same integrated visibility that NASA gives its mission controllers. Portfolio to weld. Board to bolt.
          </p>
          <div className="grantha-ctas">
            <a href="https://panorix.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="dr-cta-access">Access Panorix <span className="arrow">→</span></a>
            <Link to="/contact?p=panorix" className="btn btn-ghost" data-testid="dr-cta-demo">Request live demo <span className="arrow">→</span></Link>
            <a href="#dr-architecture" className="btn btn-ghost">See the 18 modules</a>
          </div>
          <div className="grantha-trust mono">
            Live on 6 continents · ISO 27001 · Azure + AWS + on-premise deployment
          </div>
        </div>
        <div className="grantha-hero-stage" data-testid="dr-hero-viz">
          <PanorixPerceptionField />
        </div>
      </div>
    </section>
  );
}

/* Command centre visualization — matches grantha DocumentVault pattern */
function CommandCentreViz() {
  return (
    <svg viewBox="0 0 560 560" className="grantha-vault" aria-label="Capital projects command centre visualization">
      <defs>
        <linearGradient id="ctGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E4E8C" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#2E70AD" stopOpacity="0.08" />
        </linearGradient>
      </defs>
      {/* Outer halo */}
      <circle cx="280" cy="280" r="240" fill="#1E4E8C" fillOpacity="0.04" />
      <circle cx="280" cy="280" r="200" fill="none" stroke="#1E4E8C" strokeOpacity="0.15" strokeDasharray="3 8" style={{ transformOrigin: "280px 280px", animation: "orbitSpin 50s linear infinite" }} />

      {/* Central command hexagon */}
      <g style={{ transformOrigin: "280px 280px", animation: "vaultFloat 6s ease-in-out infinite" }}>
        <polygon points="280,160 380,220 380,340 280,400 180,340 180,220" fill="url(#ctGrad)" stroke="#1E4E8C" strokeWidth="1.5" strokeOpacity="0.8" />
        <polygon points="280,160 380,220 280,280 180,220" fill="#1E4E8C" fillOpacity="0.15" stroke="#1E4E8C" strokeWidth="1" strokeOpacity="0.5" />
        <polygon points="280,280 380,220 380,340 280,400" fill="#2E70AD" fillOpacity="0.08" stroke="#2E70AD" strokeWidth="1" strokeOpacity="0.4" />

        {/* Dashboard lines inside hexagon */}
        <rect x="220" y="240" width="120" height="12" rx="2" fill="#1E4E8C" fillOpacity="0.3" />
        <rect x="220" y="258" width="80" height="8" rx="2" fill="#2E70AD" fillOpacity="0.2" />
        <rect x="220" y="272" width="100" height="8" rx="2" fill="#2E70AD" fillOpacity="0.2" />
        {/* CPI/SPI indicators */}
        <rect x="230" y="295" width="40" height="20" rx="3" fill="none" stroke="#1E4E8C" strokeOpacity="0.5" strokeWidth="0.8" />
        <text x="250" y="308" textAnchor="middle" fontSize="8" fill="#1E4E8C" fontFamily="JetBrains Mono, monospace" fontWeight="700">CPI</text>
        <rect x="280" y="295" width="40" height="20" rx="3" fill="none" stroke="#F26B1F" strokeOpacity="0.5" strokeWidth="0.8" />
        <text x="300" y="308" textAnchor="middle" fontSize="8" fill="#F26B1F" fontFamily="JetBrains Mono, monospace" fontWeight="700">SPI</text>
      </g>

      {/* Orbiting 18 module nodes (showing 9 key ones) */}
      {[
        { x: 440, y: 100, label: "SCHED" },
        { x: 510, y: 210, label: "DRONE" },
        { x: 510, y: 350, label: "COST" },
        { x: 440, y: 460, label: "HSE" },
        { x: 280, y: 500, label: "MATL" },
        { x: 120, y: 460, label: "CLM" },
        { x: 50, y: 350, label: "5D" },
        { x: 50, y: 210, label: "WKFNT" },
        { x: 120, y: 100, label: "PORT" },
      ].map((n, i) => (
        <g key={i} style={{ animation: `vaultNode 4s ease-in-out ${i * 0.2}s infinite` }}>
          <line x1="280" y1="280" x2={n.x} y2={n.y} stroke="#2E70AD" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2 4" />
          <circle cx={n.x} cy={n.y} r="18" fill="var(--bg-elev)" stroke="#1E4E8C" strokeWidth="1.5" />
          <text x={n.x} y={n.y + 3} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="8" fontWeight="700" fill="#1E4E8C">{n.label}</text>
          <circle r="2.5" fill="#F26B1F" style={{ offsetPath: `path('M 280 280 L ${n.x} ${n.y}')`, animation: `pipeFlow ${3 + i * 0.3}s linear infinite`, animationDelay: `${i * 0.4}s` }} />
        </g>
      ))}

      {/* Label */}
      <text x="280" y="145" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#F26B1F" letterSpacing="3" fontWeight="700">18 MODULES · LIVE · ₹14,280 Cr</text>
    </svg>
  );
}

/* ═══════ S2 PROBLEM — matches grantha-problem layout ═══════ */
function Section2Problem() {
  const stats = [
    { n: 12, suffix: "+", label: "disconnected tools on a typical ₹5k Cr project (schedule, cost, 3D, drone, weld, material, RAID, HSE...)" },
    { n: 420, prefix: "₹", suffix: " Cr", label: "cost of tool sprawl on a typical ₹5k Cr EPC" },
    { n: 42, suffix: "%", label: "of management reviews spent reconciling data, not making decisions" },
    { n: 4.6, suffix: " days", label: "average time to reconcile EVM data across tools", decimals: 1 },
  ];
  return (
    <section className="grantha-problem" data-testid="dr-imperative">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--ember)" }}>The control-tower imperative</div>
        <h2 className="grantha-problem-h">
          <em>On a ₹5,000 Cr project, seven tools hold the truth. Nobody holds the truth about all seven.</em>
        </h2>
        <div className="grantha-problem-grid">
          {stats.map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`dr-stat-${i}`}>
              <div className="grantha-problem-num">
                <CountUp end={s.n} prefix={s.prefix || ""} suffix={s.suffix || ""} decimals={s.decimals || 0} />
              </div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote">
          Seven tools. Seven dashboards. <em>Seven versions of truth.</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S3 MODULES — matches grantha-arch layer-tab layout ═══════ */
function Section3Modules() {
  const [activeTier, setActiveTier] = useState(1);
  return (
    <section className="grantha-arch" id="dr-architecture" data-testid="dr-modules">
      <div className="container">
        <div className="eyebrow">Platform architecture</div>
        <h2 className="grantha-arch-h">
          Eighteen modules. Six tiers. <em>One command canvas.</em>
        </h2>
        <p className="grantha-arch-sub">
          Hover or click any tier to explore. Panorix's eighteen modules organise into six logical tiers — from portfolio governance through scheduling &amp; progress, construction execution, cost &amp; commercial, material &amp; delivery, to command &amp; cognition.
        </p>

        <div className="grantha-layers" data-testid="dr-tiers">
          {TIERS.map((T) => (
            <button
              key={T.n}
              type="button"
              className={"grantha-layer " + (activeTier === T.n ? "is-active" : "")}
              onMouseEnter={() => setActiveTier(T.n)}
              onClick={() => setActiveTier(T.n)}
              data-testid={`dr-tier-${T.n}`}
            >
              <span className="grantha-layer-n mono">TIER · 0{T.n}</span>
              <span className="grantha-layer-name">{T.name}</span>
              <span className="grantha-layer-count mono">{MODULES.filter(m => m.tier === T.n).length}</span>
            </button>
          ))}
        </div>

        <div className="grantha-modules" data-testid="dr-mod-grid">
          {MODULES.filter(m => m.tier === activeTier).map((m) => (
            <div key={m.slug} className="grantha-module" data-testid={`dr-mod-${m.slug}`}>
              <div className="grantha-module-icon"><m.Icon size={22} /></div>
              <div className="grantha-module-body">
                <div className="grantha-module-name">{m.name}</div>
                <div className="grantha-module-desc">{m.body}</div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontFamily: "var(--f-serif)", fontStyle: "italic", fontSize: 18, color: "var(--text-2)", textAlign: "center", marginTop: 40 }}>
          Eighteen modules. Each stands alone. Together, they form one cognitive substrate for capital projects.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S4 DASHBOARD — matches grantha-viewer-grid (visual left, copy right) ═══════ */
function Section4Dashboard() {
  const features = [
    { n: "01", t: "Portfolio-level health score", b: "Aggregated Cost/Schedule/Quality/HSE metrics across all active projects with board-ready exports." },
    { n: "02", t: "Integrated CPM/Gantt (industry-standard scheduler parity)", b: "Full CPM forward/backward pass, critical path, resource loading, bi-directional legacy schedulers XER and legacy schedulers sync." },
    { n: "03", t: "4D Timeliner + APS/Forge", b: "Watch construction sequence on the 3D model. Schedule-vs-3D linkage, clash detection over time." },
    { n: "04", t: "Real-time EVM (CPI/SPI)", b: "Continuous CPI/SPI with configurable thresholds. Period-over-period trending and EAC/ETC forecasting." },
    { n: "05", t: "Cognara AI integration", b: "Natural-language queries across all 18 modules. Anomaly detection, delay analysis, contract intelligence, workfront predictions." },
    { n: "06", t: "Role-based command centre", b: "Custom widgets for Owner, EPC, PMC, Site views. Alert engine with escalation rules and SLA tracking." },
    { n: "07", t: "Workfront readiness scoring", b: "AWP-based IWP scoring against 8 constraints. Rolling 3-week lookahead. 15-25% crew productivity improvement." },
    { n: "08", t: "Drone & LiDAR deviation analysis", b: "Weekly captures auto-aligned to 3D model. As-built vs as-planned heatmaps. Volumetric progress measurement." },
  ];
  return (
    <section className="grantha-viewer" data-testid="dr-dashboard">
      <div className="container grantha-viewer-grid">
        <div className="grantha-viewer-visual">
          <div className="grantha-viewer-frame">
            <div className="grantha-viewer-bar">
              <span className="mono">Panorix · Jamnagar Refinery · Week 47</span>
              <span className="mono" style={{ color: "var(--ember)" }}>● 3 ALERTS</span>
            </div>
            <svg viewBox="0 0 420 280" className="grantha-viewer-svg">
              <rect width="420" height="280" fill="var(--bg-sunk)" />
              {/* Dashboard mockup */}
              {/* Health score */}
              <rect x="15" y="15" width="120" height="90" rx="6" fill="none" stroke="var(--steel)" strokeWidth="0.8" strokeOpacity="0.5" />
              <text x="75" y="35" textAnchor="middle" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">PROJECT HEALTH</text>
              <text x="75" y="72" textAnchor="middle" fontSize="28" fill="var(--steel)" fontFamily="var(--f-mono)" fontWeight="700">78</text>
              <text x="75" y="85" textAnchor="middle" fontSize="8" fill="var(--text-3)" fontFamily="var(--f-mono)">/100</text>

              {/* EVM tile */}
              <rect x="145" y="15" width="120" height="90" rx="6" fill="none" stroke="var(--steel)" strokeWidth="0.8" strokeOpacity="0.5" />
              <text x="205" y="35" textAnchor="middle" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">EVM</text>
              <text x="180" y="58" fontSize="10" fill="#10B981" fontFamily="var(--f-mono)" fontWeight="600">CPI 1.04 ▲</text>
              <text x="180" y="74" fontSize="10" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="600">SPI 0.97 ▼</text>
              <text x="180" y="90" fontSize="8" fill="var(--text-3)" fontFamily="var(--f-mono)">EV: ₹2,847 Cr</text>

              {/* 3D model area */}
              <rect x="275" y="15" width="130" height="130" rx="6" fill="none" stroke="var(--steel)" strokeWidth="0.8" strokeOpacity="0.3" />
              <text x="340" y="32" textAnchor="middle" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">3D PROGRESS</text>
              <rect x="300" y="55" width="25" height="60" rx="2" fill="var(--steel)" fillOpacity="0.3" />
              <rect x="330" y="45" width="20" height="70" rx="2" fill="var(--steel)" fillOpacity="0.4" />
              <rect x="355" y="50" width="22" height="65" rx="2" fill="#F26B1F" fillOpacity="0.25" stroke="#F26B1F" strokeWidth="0.5" />
              <text x="366" y="125" textAnchor="middle" fontSize="6" fill="#F26B1F" fontFamily="var(--f-mono)">T-201</text>

              {/* S-curve */}
              <rect x="15" y="115" width="250" height="90" rx="6" fill="none" stroke="var(--steel)" strokeWidth="0.8" strokeOpacity="0.3" />
              <text x="30" y="130" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">S-CURVE</text>
              <path d="M30 190 Q80 185 130 165 T230 140" fill="none" stroke="var(--steel)" strokeWidth="1.2" />
              <path d="M30 190 Q80 188 130 172 T230 155" fill="none" stroke="var(--steel)" strokeWidth="0.8" strokeDasharray="3 2" />
              <path d="M30 190 Q80 186 130 170 T230 148" fill="none" stroke="#F26B1F" strokeWidth="1" />

              {/* AI chat */}
              <rect x="275" y="155" width="130" height="110" rx="6" fill="none" stroke="var(--steel)" strokeWidth="0.8" strokeOpacity="0.3" />
              <text x="290" y="170" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)">COGNARA AI</text>
              <text x="290" y="188" fontSize="6" fill="var(--text-2)" fontFamily="var(--f-sans)">T-201 slip: vendor delay.</text>
              <text x="290" y="200" fontSize="6" fill="var(--text-2)" fontFamily="var(--f-sans)">Probability: 73%.</text>
              <text x="290" y="212" fontSize="6" fill="var(--text-2)" fontFamily="var(--f-sans)">Rec: expedite LC amendment</text>
              <rect x="285" y="240" width="110" height="16" rx="3" fill="none" stroke="var(--steel)" strokeOpacity="0.2" strokeWidth="0.5" />
              <text x="295" y="251" fontSize="6" fill="var(--text-3)" fontFamily="var(--f-mono)">Ask a question...</text>
            </svg>
            <div className="grantha-viewer-meta mono">
              <div>18 modules · 847 active users · Last sync: 12s ago</div>
              <div style={{ color: "var(--ember)" }}>Alert · T-201 schedule slip · Day 847</div>
            </div>
          </div>
        </div>
        <div className="grantha-viewer-copy">
          <div className="eyebrow">The command canvas</div>
          <h2 className="grantha-viewer-h"><em>Mission control</em> for capital projects.</h2>
          <div className="grantha-features">
            {features.map((f) => (
              <div className="grantha-feature" key={f.n}>
                <span className="grantha-feature-n mono">{f.n}</span>
                <div>
                  <div className="grantha-feature-t">{f.t}</div>
                  <div className="grantha-feature-b">{f.b}</div>
                </div>
              </div>
            ))}
          </div>
          <blockquote className="grantha-pull">
            Legacy tools were built to record. <em>Panorix was built to decide.</em>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S5 COGNITIVE — matches grantha-wf (dark band, split layout) ═══════ */
function Section5Cognitive() {
  const [activeAgent, setActiveAgent] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setActiveAgent(a => (a + 1) % 5), 3000);
    return () => clearInterval(iv);
  }, []);

  const agents = [
    { label: "Detect", text: "Weld NDT spike Unit-1" },
    { label: "Query", text: "IWPs buildable next wk?" },
    { label: "Analyze", text: "Drone 8% earthwork lag" },
    { label: "Draft", text: "Claim #47 in 14s" },
    { label: "Forecast", text: "P80: Jun-2028 ±3wk" },
  ];

  return (
    <section className="grantha-wf" data-testid="dr-cognitive">
      <div className="container grantha-wf-grid">
        <div>
          <div className="eyebrow" style={{ color: "var(--steel-200)" }}>The cognitive layer</div>
          <h2 className="grantha-wf-h">Panorix doesn't just display data. <em>It reasons about it.</em></h2>
          <p className="grantha-wf-body">
            Powered by Cognara — Capexara's multi-agent GenAI system trained on FIDIC, NEC, CII best practices, and 50+ years of capital project post-mortems. Five cognitive capabilities embedded across all 18 modules.
          </p>
          <ul className="grantha-wf-list">
            {["Anomaly detection — CPI drops, weld NDT spikes, drone earthwork divergence, RAID escalations", "Natural-language query — 'Which IWPs are buildable next week?', 'Show RAID items pending past 14 days'", "Delay analysis — traces from RAID risks to missing materials to workforce constraints to weld quality", "Contract intelligence — RAG over your contract corpus for clause citations and claim drafting", "Monte Carlo forecasting — schedule, cost, AND workfront availability (RAID + material + workforce)"].map((c, i) => (
              <li key={i} style={{ color: activeAgent === i ? "#F26B1F" : undefined, transition: "color 0.3s" }}>{c}</li>
            ))}
          </ul>
        </div>
        <div className="grantha-wf-diagram" data-testid="dr-cog-graph">
          <svg viewBox="0 0 360 360" style={{ width: "100%", maxWidth: 360, margin: "0 auto", display: "block" }}>
            <polygon points="180,140 210,157 210,190 180,207 150,190 150,157" fill="rgba(30,78,140,0.15)" stroke="#1E4E8C" strokeWidth="1.5" />
            <text x="180" y="178" textAnchor="middle" fontSize="9" fill="#6ac8ff" fontFamily="var(--f-mono)">PANORIX</text>
            {agents.map((agent, i) => {
              const angle = (i * 72 - 90) * (Math.PI / 180);
              const x = 180 + 110 * Math.cos(angle);
              const y = 173 + 110 * Math.sin(angle);
              const isActive = activeAgent === i;
              return (
                <g key={i}>
                  <line x1="180" y1="173" x2={x} y2={y} stroke={isActive ? "#F26B1F" : "rgba(30,78,140,0.3)"} strokeWidth={isActive ? 1.5 : 0.8} />
                  <circle cx={x} cy={y} r={isActive ? 22 : 18} fill={isActive ? "rgba(242,107,31,0.12)" : "rgba(30,78,140,0.08)"} stroke={isActive ? "#F26B1F" : "#1E4E8C"} strokeWidth={isActive ? 1.5 : 1} />
                  <text x={x} y={y - 3} textAnchor="middle" fontSize="8" fill={isActive ? "#F26B1F" : "#9AA2B1"} fontFamily="var(--f-mono)">{agent.label}</text>
                  {isActive && <text x={x} y={y + 10} textAnchor="middle" fontSize="6" fill="rgba(243,244,246,0.6)" fontFamily="var(--f-mono)">{agent.text}</text>}
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S6 INTEGRATIONS — matches grantha-integ layout ═══════ */
function Section6Integrations() {
  const groups = [
    { head: "Scheduling", items: ["legacy schedulers-class schedulers (XER round-trip)", "legacy schedulers-class schedulers (MPP round-trip)", "Asta-class schedulers", "schedule-risk tools"] },
    { head: "Cost & ERP", items: ["enterprise ERP · PS · PM · MM · FI/CO", "enterprise ERP · Unifier", "enterprise ERP", "Ecosys · InEight"] },
    { head: "3D / BIM / Drone", items: ["Autodesk-class CAD/BIM BIM stack · ACC · BIM 360", "3D plant-design suites · digital-twin platforms", "engineering 3D suites · SDx · structural-design tools", "DJI · Skydio · Leica BLK · FARO"] },
    { head: "Field & Materials", items: ["WeldEye · ArcWorks (weld mgmt)", "RFID/QR systems (spool/material)", "field-engineering tools · fab-shop tools (fab shops)", "document libraries · engineering-document tools · Docuweave"] },
  ];
  return (
    <section className="grantha-integ" data-testid="dr-integrations">
      <div className="container">
        <div className="eyebrow">Built for your stack</div>
        <h2 className="grantha-integ-h">Integrates with the engineering tools <em>your teams already use.</em></h2>
        <div className="grantha-integ-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {groups.map((g, i) => (
            <div className="grantha-integ-col" key={i}>
              <div className="grantha-integ-head mono">{g.head}</div>
              <ul className="grantha-integ-list">
                {g.items.map((it) => <li key={it}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S7 INDUSTRIES — matches grantha-inds layout ═══════ */
function Section7Industries() {
  const chips = [
    "Oil, Gas & Energy — Upstream · Pipeline · LNG · Downstream · Hydrogen",
    "Power & Utilities — Generation · T&D · BESS · Desalination",
    "Chemicals — Basic · Specialty",
    "Infrastructure — Roads · Metros · Data Centers · Bridges",
    "Metals, Mining & Minerals",
    "Manufacturing — Discrete · Life Sciences · P&P",
    "Marine — Ports · Shipbuilding",
  ];
  return (
    <section className="grantha-inds" data-testid="dr-industries">
      <div className="container">
        <div className="eyebrow">Industries served</div>
        <h2 className="grantha-inds-h">Deployed across <em>every capital-project industry</em> we serve.</h2>
        <div className="grantha-chips">
          {chips.map((c) => <Link to="/industries" className="grantha-chip" key={c}>{c}</Link>)}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S8 OUTCOMES — matches grantha-out layout ═══════ */
function Section8Outcomes() {
  const cards = [
    { big: 412, prefix: "₹", suffix: " Cr", head: "average savings on a ₹5,000 Cr EPC project", body: "Workfront + Material integration + EVM discipline + claim prevention." },
    { big: 68, suffix: "%", head: "reduction in management review time on reconciliation", body: "From 42% of review time on reconciliation to <15%." },
    { big: 3.8, suffix: "× faster", head: "decision-making in executive steering committees", body: "Measured across 11 deployments in India / GCC.", decimals: 1 },
  ];
  return (
    <section className="grantha-out" data-testid="dr-outcomes">
      <div className="container">
        <div className="eyebrow">Measured impact</div>
        <h2 className="grantha-out-h">The business case, <em>validated on real projects.</em></h2>
        <div className="grantha-out-grid">
          {cards.map((c, i) => (
            <div className="grantha-out-card" key={i} data-testid={`dr-out-${i}`}>
              <div className="grantha-out-num">
                <CountUp end={c.big} prefix={c.prefix || ""} suffix={c.suffix || ""} decimals={c.decimals || 0} />
              </div>
              <div className="grantha-out-head">{c.head}</div>
              <div className="grantha-out-body">{c.body}</div>
            </div>
          ))}
        </div>
        <Link to="/resources" className="grantha-out-cta">Download the Panorix business case whitepaper <span className="arrow">→</span></Link>
      </div>
    </section>
  );
}

/* ═══════ S9 FAQ — matches grantha-faq layout ═══════ */
function Section9Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="grantha-faq" data-testid="dr-faq-section">
      <div className="container">
        <div className="eyebrow">Frequently asked</div>
        <h2 className="grantha-faq-h">Everything a <em>CPO or CIO</em> wants to know about Panorix.</h2>
        <div className="grantha-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={"grantha-faq-item " + (isOpen ? "is-open" : "")} data-testid={`dr-faq-${i}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="grantha-faq-q" aria-expanded={isOpen}>
                  <span>{f.q}</span>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </button>
                {isOpen && <div className="grantha-faq-a" data-testid={`dr-faq-answer-${i}`}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S10 CTA — matches grantha-cta layout ═══════ */
function Section10Cta() {
  return (
    <section className="grantha-cta" data-testid="dr-cta">
      <div className="container grantha-cta-inner">
        <div className="eyebrow" style={{ color: "#F26B1F" }}>See Panorix in mission mode</div>
        <h2 className="grantha-cta-h">Bring us your hardest project — <em>we'll show you mission control for it, live.</em></h2>
        <p className="grantha-cta-sub">
          Our "Mission Control Demo" is a 60-minute live session where we load your actual project data (under NDA), configure Panorix's modules to your scope, and walk your team through the integrated command canvas. No sanitized demos. Your data. Live.
        </p>
        <div className="grantha-cta-row">
          <Link to="/contact?p=panorix" className="btn btn-primary grantha-cta-btn" data-testid="dr-cta-mission">Book a Mission Control Demo <span className="arrow">→</span></Link>
          <Link to="/resources" className="btn btn-ghost grantha-cta-ghost">Download datasheet (PDF)</Link>
        </div>
      </div>
    </section>
  );
}
